import { useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { SkeletonGrid, EmptyState } from './ui';
import { cx } from '../lib/format';
import { fallbackImage } from '../lib/fallbackImages';

/**
 * Card image that survives a dead URL.
 *
 * A card's `image` is "resolved" only in the sense that it is non-null — the
 * file behind it can still be missing (the API and its storage directory can
 * and do drift apart), and a 404 renders as a blank box with no alt text: ugly
 * for sighted users, invisible to assistive tech. So the component, not the
 * caller, owns recovery.
 *
 * Two properties this has to hold:
 *
 *  - **No error loop.** The failure is remembered as *which URL* failed. Once
 *    the swap has happened, `resolved` is the fallback, and the guard below
 *    returns without touching state — so no further `src` change, and therefore
 *    no further `error` event, can be triggered. Worst case is two loads. This
 *    also covers the case where `image` already *is* the fallback (a record with
 *    no image at all): the very first error short-circuits.
 *  - **Deterministic.** The stand-in is chosen from the card's fixed grid index,
 *    so it is the same photo the card would have shown had it never had an image
 *    reference, and it does not shuffle between renders.
 *
 * Keying on the URL rather than a boolean also means a genuinely new `image`
 * prop gets its own attempt instead of inheriting the old one's failure.
 */
function CardImage({ src, index, className }) {
  const [failedSrc, setFailedSrc] = useState(null);
  const fallback = fallbackImage(index);
  // A missing `src` takes the same path as a dead one, so the band never emits
  // an `<img>` with no source regardless of what a caller hands it.
  const resolved = !src || failedSrc === src ? fallback : src;

  return (
    <img
      src={resolved}
      alt=""
      loading="lazy"
      className={className}
      onError={() => {
        if (resolved === fallback) return; // already stood in — stop, don't re-fire
        setFailedSrc(src);
      }}
    />
  );
}

/**
 * One "<Something> & Highlights" band on the landing page: eyebrow + heading,
 * a "view all" link, and a four-up card grid.
 *
 * Deliberately content-agnostic — News, Activities and Events all render
 * through it, so the three bands cannot drift apart visually. Callers pass
 * already-normalized items:
 *   { id, to, image, badge, title, text, pill }
 * `image` should be the record's best candidate URL; the band recovers on its
 * own if that URL turns out to be dead (see `CardImage`), so callers do not
 * have to pre-verify it. `badge`, `text` and `pill` are optional and simply
 * omitted when null.
 */
export default function HighlightsSection({
  eyebrow,
  title,
  viewAllTo,
  viewAllLabel,
  loading = false,
  items = [],
  cta = 'Read more',
  emptyTitle,
  emptyMessage,
  className,
}) {
  return (
    <section className={cx('section', className)}>
      <div className="container-page">
        <Reveal className="mb-9 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-gold-600">{eyebrow}</p>
            <h2 className="mt-2 font-serif text-3xl font-bold text-maroon-800 sm:text-4xl">{title}</h2>
          </div>
          <Link
            to={viewAllTo}
            className="text-xs font-bold uppercase tracking-[0.16em] text-maroon-700 transition hover:text-gold-600"
          >
            {viewAllLabel} <span aria-hidden="true">&rsaquo;</span>
          </Link>
        </Reveal>

        {loading ? (
          // `columns` mirrors the loaded grid below exactly; if one changes the
          // other must, or the band reflows when the data lands.
          <SkeletonGrid count={4} columns={4} />
        ) : items.length ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item, i) => (
              // `min-w-0` clears the grid item's automatic (content-based) minimum
              // width, so a single long unbroken word in `title` or `text` cannot
              // stretch its column and push the page into horizontal scroll.
              <Reveal key={item.id} delay={i * 80} className="h-full min-w-0">
                <Link to={item.to} className="card card-hover group flex h-full flex-col overflow-hidden">
                  <div className="relative aspect-[4/3] overflow-hidden bg-maroon-50">
                    <CardImage
                      src={item.image}
                      index={i}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {item.badge && (
                      <span className="absolute left-3 top-3 rounded-full bg-maroon-900/85 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-wider text-gold-300 backdrop-blur">
                        {item.badge}
                      </span>
                    )}
                    {item.pill && (
                      <span className="absolute right-3 top-3 rounded-full bg-gold-400 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-wider text-maroon-900">
                        {item.pill}
                      </span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col p-5">
                    {/* `break-words` on both text slots: callers pass arbitrary
                        record fields (titles, locations, summaries), and a long
                        unbroken token must wrap inside the card rather than
                        overflow it. */}
                    <h3 className="break-words font-display text-lg font-bold leading-snug text-ink transition-colors group-hover:text-maroon-700">
                      {item.title}
                    </h3>
                    {item.text && <p className="mt-2 break-words text-sm text-ink-muted">{item.text}</p>}
                    <span className="mt-auto pt-4 text-xs font-bold uppercase tracking-[0.16em] text-maroon-700 transition group-hover:text-gold-600">
                      {cta} <span aria-hidden="true">&rsaquo;</span>
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        ) : (
          <EmptyState title={emptyTitle} message={emptyMessage} />
        )}
      </div>
    </section>
  );
}
